'use client'

import Link from 'next/link'
import styled, { css } from 'styled-components'

import type { ProductListItem } from '../../api/generated'
import { colors } from '../../constants/colors'
import { breakpoints } from '../breakPoints'

export type ProductListCardVariant = 'card' | 'row'

export interface ProductListCardProps {
  badgeImageUrl?: string
  className?: string
  href?: string
  isFavorite?: boolean
  location?: string
  onAddToCart?: (product: ProductListItem) => void
  onFavoriteChange?: (product: ProductListItem, isFavorite: boolean) => void
  onMessage?: (product: ProductListItem) => void
  ownerName?: string
  product: ProductListItem
  variant?: ProductListCardVariant
}

const priceFormatter = new Intl.NumberFormat('ru-RU', {
  maximumFractionDigits: 0,
})

export function ProductListCard({
  badgeImageUrl,
  className,
  href,
  isFavorite = false,
  location,
  onAddToCart,
  onFavoriteChange,
  onMessage,
  ownerName,
  product,
  variant = 'card',
}: ProductListCardProps) {
  const productHref = href || (product.productId ? `/item/${product.productId}` : '/')
  const imageUrl = product.imagesUrls?.[0]
  const { period, price } = getProductPrice(product)
  const formattedPrice =
    typeof price === 'number' ? `${priceFormatter.format(price)} ₽` : 'Цена не указана'
  const mobileCard = (
    <MobileCard className={className}>
      <MobileImageWrapper>
        <ProductImage href={productHref} imageUrl={imageUrl} />
        <MobileFavoriteButton
          $active={isFavorite}
          aria-label={isFavorite ? 'Удалить из избранного' : 'Добавить в избранное'}
          onClick={() => onFavoriteChange?.(product, !isFavorite)}
          type={'button'}
        >
          <img alt={''} src={'/images/product-list-card/heart.svg'} />
        </MobileFavoriteButton>
      </MobileImageWrapper>

      <MobileContent>
        <MobileTitleLink href={productHref}>{product.name || 'Без названия'}</MobileTitleLink>
        <MobilePriceRow>
          <MobilePrice>{formattedPrice}</MobilePrice>
          {period && <MobilePeriod>{period}</MobilePeriod>}
        </MobilePriceRow>
        {product.description && <MobileDescription>{product.description}</MobileDescription>}
      </MobileContent>

      <MobileOwnerPanel>
        {location && <MobileLocation>{location}</MobileLocation>}
        <MobileOwnerRow>
          <MobileOwnerName>{ownerName || 'Владелец'}</MobileOwnerName>
          <MobileRating>
            <MobileRatingStar aria-hidden={'true'}>★</MobileRatingStar>
            <span>
              {product.rating || 0} · {product.reviewsCount || 0} отзыва
            </span>
          </MobileRating>
        </MobileOwnerRow>
        <MobileActions>
          <MessageButton onClick={() => onMessage?.(product)} type={'button'}>
            Написать
          </MessageButton>
          <CartButton onClick={() => onAddToCart?.(product)} type={'button'}>
            Добавить в корзину
          </CartButton>
        </MobileActions>
      </MobileOwnerPanel>
    </MobileCard>
  )

  if (variant === 'row') {
    return (
      <>
        {mobileCard}
        <RowCard className={className}>
          <RowImageWrapper>
            <ProductImage href={productHref} imageUrl={imageUrl} />
            <FavoriteButton
              $active={isFavorite}
              aria-label={isFavorite ? 'Удалить из избранного' : 'Добавить в избранное'}
              onClick={() => onFavoriteChange?.(product, !isFavorite)}
              type={'button'}
            >
              <img alt={''} src={'/images/product-list-card/heart.svg'} />
            </FavoriteButton>
          </RowImageWrapper>

          <RowDetails>
            <div>
              <RowTitleLink href={productHref}>{product.name || 'Без названия'}</RowTitleLink>
              <RowPrice>{formattedPrice}</RowPrice>
              <PricePeriod>{period}</PricePeriod>
            </div>

            <RowDescription>
              {location && <RowLocation>{location}</RowLocation>}
              {product.description && <Description>{product.description}</Description>}
            </RowDescription>
          </RowDetails>

          <OwnerPanel>
            <OwnerInfo>
              <OwnerName>{ownerName || 'Владелец'}</OwnerName>
              {(product.rating || 0) > 0 && (
                <Rating
                  alt={`Рейтинг ${product.rating} из 5`}
                  src={'/images/product-list-card/rating.svg'}
                />
              )}
              <ReviewsCount>{product.reviewsCount || 0} отзывов</ReviewsCount>
            </OwnerInfo>

            <Actions>
              <MessageButton onClick={() => onMessage?.(product)} type={'button'}>
                Написать
              </MessageButton>
              <CartButton onClick={() => onAddToCart?.(product)} type={'button'}>
                Добавить в корзину
              </CartButton>
            </Actions>
          </OwnerPanel>
        </RowCard>
      </>
    )
  }

  return (
    <>
      {mobileCard}
      <CompactCard className={className}>
        <CompactImageWrapper>
          <ProductImage href={productHref} imageUrl={imageUrl} />
          {badgeImageUrl && <BadgeImage alt={''} src={badgeImageUrl} />}
          <CompactFavoriteButton
            $active={isFavorite}
            aria-label={isFavorite ? 'Удалить из избранного' : 'Добавить в избранное'}
            onClick={() => onFavoriteChange?.(product, !isFavorite)}
            type={'button'}
          >
            <img alt={''} src={'/images/product-list-card/heart.svg'} />
          </CompactFavoriteButton>
        </CompactImageWrapper>

        <CompactContent>
          <CompactTitleRow>
            <CompactTitleLink href={productHref}>{product.name || 'Без названия'}</CompactTitleLink>
            <CompactPrice>{formattedPrice}</CompactPrice>
          </CompactTitleRow>
          {product.description && <CompactDescription>{product.description}</CompactDescription>}
        </CompactContent>

        {location && <CompactLocation>{location}</CompactLocation>}
      </CompactCard>
    </>
  )
}

function getProductPrice(product: ProductListItem) {
  if (typeof product.dayPrice === 'number') return { period: 'в день', price: product.dayPrice }
  if (typeof product.threeDaysPrice === 'number') {
    return { period: 'за 3 дня', price: product.threeDaysPrice }
  }
  if (typeof product.weekPrice === 'number') return { period: 'в неделю', price: product.weekPrice }
  if (typeof product.monthPrice === 'number')
    return { period: 'в месяц', price: product.monthPrice }

  return { period: '', price: undefined }
}

function ProductImage({ href, imageUrl }: { href: string; imageUrl?: string }) {
  return (
    <ImageLink href={href}>
      {imageUrl ? (
        <Image alt={''} src={imageUrl} />
      ) : (
        <ImagePlaceholder>
          <img alt={''} src={'/images/product-list-card/image-placeholder.svg'} />
        </ImagePlaceholder>
      )}
    </ImageLink>
  )
}

const CompactCard = styled.article`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  width: 292px;
  min-height: 254px;
  padding: 6px;
  overflow: hidden;
  border-radius: 12px;
  background: #f8f8fa;

  @media (width < ${breakpoints.productCardsDesktopMin}) {
    display: none;
  }
`

const MobileCard = styled.article`
  box-sizing: border-box;
  display: none;
  width: min(100%, 343px);
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 16px 16px;
  margin-inline: auto;

  @media (width < ${breakpoints.productCardsDesktopMin}) {
    display: flex;
  }
`

const MobileImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 138px;
  overflow: hidden;
  border-radius: 12px;
`

const CompactImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 166px;
  overflow: hidden;
  border-radius: 12px;
`

const ImageLink = styled(Link)`
  display: block;
  width: 100%;
  height: 100%;
`

const Image = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: contain;
`

const ImagePlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: #d3cef6;

  img {
    width: 25px;
    height: 25px;
  }
`

const BadgeImage = styled.img`
  position: absolute;
  top: 6px;
  left: 8px;
  width: 30px;
  height: 30px;
  border: 2px solid ${colors.white};
  border-radius: 50%;
  background: ${colors.white};
  object-fit: cover;
`

const favoriteButtonStyles = css<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: ${({ $active }) => ($active ? '#d5eded' : '#e8f5f5')};
  cursor: pointer;

  img {
    display: block;
  }
`

const CompactFavoriteButton = styled.button<{ $active: boolean }>`
  ${favoriteButtonStyles};
  position: absolute;
  bottom: 6px;
  left: 6px;
  width: 32px;
  height: 32px;
  border-radius: 8px;

  img {
    width: 20px;
    height: 20px;
  }
`

const MobileFavoriteButton = styled.button<{ $active: boolean }>`
  ${favoriteButtonStyles};
  position: absolute;
  bottom: 6px;
  left: 6px;
  width: 36px;
  height: 36px;
  border-radius: 10px;

  img {
    width: 24px;
    height: 24px;
  }
`

const MobileContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

const MobileTitleLink = styled(Link)`
  overflow: hidden;
  width: 100%;
  color: ${colors.black};
  font-size: 16px;
  font-weight: 500;
  line-height: 1.2;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const MobilePriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`

const MobilePrice = styled.p`
  margin: 0;
  color: ${colors.black};
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
`

const MobilePeriod = styled.span`
  color: ${colors.grey};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
`

const MobileDescription = styled.p`
  display: -webkit-box;
  margin: 0;
  overflow: hidden;
  color: ${colors.grey};
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
`

const MobileOwnerPanel = styled.aside`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  padding: 14px 12px 12px;
  border-radius: 12px;
  background: #f8f8fa;
`

const MobileLocation = styled.p`
  margin: 0;
  color: ${colors.black};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
`

const MobileOwnerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`

const MobileOwnerName = styled.p`
  min-width: 0;
  margin: 0;
  overflow: hidden;
  color: ${colors.black};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const MobileRating = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 4px;
  color: ${colors.grey};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
`

const MobileRatingStar = styled.span`
  color: #f6a609;
  font-size: 20px;
  line-height: 1;
`

const MobileActions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  width: 100%;

  button {
    min-height: 32px;
    padding: 5px 8px;
    white-space: nowrap;
  }
`

const CompactContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
`

const CompactTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`

const compactTextStyles = css`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
  letter-spacing: 0.28px;
  color: ${colors.black};
`

const CompactTitleLink = styled(Link)`
  ${compactTextStyles};
  min-width: 0;
  overflow: hidden;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const CompactPrice = styled.p`
  ${compactTextStyles};
  flex-shrink: 0;
`

const CompactDescription = styled.p`
  width: 100%;
  margin: 0;
  overflow: hidden;
  color: ${colors.grey};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: 0.24px;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const CompactLocation = styled.p`
  ${compactTextStyles};
  width: 100%;
`

const RowCard = styled.article`
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  max-width: 1200px;
  min-width: 700px;
  height: 211px;

  @media (width < ${breakpoints.productCardsDesktopMin}) {
    display: none;
  }
`

const RowImageWrapper = styled.div`
  position: relative;
  flex: 0 0 206px;
  width: 206px;
  height: 210px;
  overflow: hidden;
  border-radius: 12px;
`

const FavoriteButton = styled.button<{ $active: boolean }>`
  ${favoriteButtonStyles};
  position: absolute;
  bottom: 6px;
  left: 6px;
  width: 38px;
  height: 38px;
  border-radius: 10px;

  img {
    width: 24px;
    height: 24px;
  }
`

const RowDetails = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
  height: 210px;
`

const RowTitleLink = styled(Link)`
  display: block;
  overflow: hidden;
  color: ${colors.black};
  font-size: 24px;
  font-weight: 600;
  line-height: 26.8px;
  letter-spacing: 0.48px;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RowPrice = styled.p`
  margin: 12px 0 0;
  color: ${colors.black};
  font-size: 24px;
  font-weight: 700;
  line-height: 26.8px;
  letter-spacing: 0.48px;
`

const PricePeriod = styled.p`
  margin: 0;
  color: ${colors.grey};
  font-size: 14px;
  font-weight: 500;
  line-height: 14px;
  letter-spacing: 0.56px;
`

const RowDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`

const RowLocation = styled.p`
  margin: 0;
  color: ${colors.black};
  font-size: 16px;
  font-weight: 600;
  line-height: 26.8px;
  letter-spacing: 0.32px;
`

const Description = styled.p`
  display: -webkit-box;
  margin: 0;
  overflow: hidden;
  color: ${colors.grey};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: 0.28px;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
`

const OwnerPanel = styled.aside`
  box-sizing: border-box;
  display: flex;
  flex: 0 0 218px;
  flex-direction: column;
  justify-content: space-between;
  width: 218px;
  height: 210px;
  padding: 16px;
  border-radius: 12px;
  background: #f8f8fa;
`

const OwnerInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`

const OwnerName = styled.p`
  margin: 0;
  color: ${colors.black};
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: 0.32px;
`

const Rating = styled.img`
  display: block;
  width: 89px;
  height: 15px;
`

const ReviewsCount = styled.p`
  margin: 0;
  color: ${colors.grey};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: 0.28px;
`

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 32px;
  padding: 5px 16px;
  border: 0;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  cursor: pointer;
`

const MessageButton = styled(ActionButton)`
  background: #e8f5f5;
  color: ${colors.primary};
`

const CartButton = styled(ActionButton)`
  background: ${colors.primary};
  color: ${colors.white};
`
